import { ColumnWidthOutlined } from "@ant-design/icons"
import { Button, Dropdown, type MenuProps, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { homeColor } from "../../home-theme.ts"
import type { HomeCardEdit } from "../HomeCard.tsx"
import type { HomeSectionId } from "../homeSectionModel.ts"
import { WIDTH_PRESETS, type WidthPreset } from "./homeLayout"
import { WidgetDragHandle, WidgetRemoveButton } from "./WidgetEditChrome"

export interface UnitLayoutCommands {
  /** The card's own name, so every control announces what it acts on. */
  title: string
  width: number
  minWidth: number
  section: HomeSectionId
  sections: { id: HomeSectionId; label: string }[]
  setWidth: (preset: WidthPreset) => void
  taller: () => void
  shorter: () => void
  move: (direction: "earlier" | "later") => void
  nudge: (direction: "left" | "right") => void
  moveToSection: (section: HomeSectionId) => void
  remove: () => void
}

const PRESET_ORDER: WidthPreset[] = ["third", "half", "twoThirds", "full"]

const PRESET_LABEL: Record<WidthPreset, { key: string; fallback: string }> = {
  third: { key: "home.layout.width.third", fallback: "1/3 width" },
  half: { key: "home.layout.width.half", fallback: "1/2 width" },
  twoThirds: { key: "home.layout.width.twoThirds", fallback: "2/3 width" },
  full: { key: "home.layout.width.full", fallback: "Full width" }
}

/**
 * The layout menu: everything a drag or a resize can do, as menu items, so
 * the whole layout is editable from the keyboard. antd's menu is itself
 * keyboard-navigable (arrows, Enter, Escape), and every item names its
 * effect rather than showing an icon alone.
 */
const UnitLayoutMenu = ({ commands }: { commands: UnitLayoutCommands }) => {
  const { t } = useTranslation()
  const items: MenuProps["items"] = [
    {
      type: "group",
      key: "width",
      label: t("home.layout.widthGroup", "Width"),
      children: PRESET_ORDER.map((preset) => {
        const tooNarrow = WIDTH_PRESETS[preset] < commands.minWidth
        return {
          key: `width:${preset}`,
          disabled: tooNarrow,
          label:
            (commands.width === WIDTH_PRESETS[preset] ? "✓ " : "") +
            t(PRESET_LABEL[preset].key, PRESET_LABEL[preset].fallback) +
            (tooNarrow
              ? ` — ${t("home.layout.tooNarrow", "too narrow for this card")}`
              : "")
        }
      })
    },
    {
      type: "group",
      key: "height",
      label: t("home.layout.heightGroup", "Height"),
      children: [
        { key: "taller", label: t("home.layout.taller", "Taller") },
        { key: "shorter", label: t("home.layout.shorter", "Shorter") }
      ]
    },
    {
      type: "group",
      key: "position",
      label: t("home.layout.positionGroup", "Position"),
      children: [
        { key: "earlier", label: t("home.layout.moveEarlier", "Move up") },
        { key: "later", label: t("home.layout.moveLater", "Move down") },
        {
          key: "left",
          label: t("home.layout.moveLeft", "Move left"),
          disabled: commands.width >= 12
        },
        {
          key: "right",
          label: t("home.layout.moveRight", "Move right"),
          disabled: commands.width >= 12
        }
      ]
    },
    {
      key: "section",
      label: t("home.layout.moveToSection", "Move to section"),
      children: commands.sections
        .filter((section) => section.id !== commands.section)
        .map((section) => ({
          key: `section:${section.id}`,
          label: section.label
        }))
    }
  ]

  const onClick: MenuProps["onClick"] = ({ key }) => {
    if (key.startsWith("width:"))
      commands.setWidth(key.slice("width:".length) as WidthPreset)
    else if (key.startsWith("section:"))
      commands.moveToSection(key.slice("section:".length) as HomeSectionId)
    else if (key === "taller") commands.taller()
    else if (key === "shorter") commands.shorter()
    else if (key === "earlier" || key === "later") commands.move(key)
    else if (key === "left" || key === "right") commands.nudge(key)
  }

  return (
    <Dropdown menu={{ items, onClick }} trigger={["click"]}>
      <Tooltip title={t("home.layout.menu", "Size and position")}>
        <Button
          type="text"
          size="small"
          data-testid="home-unit-layout-menu"
          aria-label={t(
            "home.layout.menuNamed",
            "Size and position of {{title}}",
            {
              title: commands.title
            }
          )}
          icon={<ColumnWidthOutlined />}
          style={{
            width: 24,
            height: 24,
            minWidth: 24,
            color: homeColor.inkMuted
          }}
        />
      </Tooltip>
    </Dropdown>
  )
}

/** The handle, the layout menu and Remove for one card's header. */
export const unitEditChrome = (commands: UnitLayoutCommands): HomeCardEdit => ({
  handle: (
    <WidgetDragHandle
      title={commands.title}
      onMove={(direction) => {
        if (direction === "up") commands.move("earlier")
        else if (direction === "down") commands.move("later")
        else commands.nudge(direction)
      }}
    />
  ),
  controls: (
    <>
      <UnitLayoutMenu commands={commands} />
      <WidgetRemoveButton title={commands.title} onRemove={commands.remove} />
    </>
  )
})
